import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export function pad(n) { return String(n).padStart(2, "0"); }
export function toISODate(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
export function fmtDateLong(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
export function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
export function isValidPhone(p) { return /^[+\d][\d\s\-]{6,20}$/.test(p); }

export function CalendarPicker({ settings, onPick, selectedDate }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + (settings?.rolling_window_days || 30));
    return d;
  }, [today, settings]);

  const days = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const last = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    return arr;
  }, [viewMonth]);

  const isPickable = (d) => {
    if (!d) return false;
    if (d < today) return false;
    if (d > maxDate) return false;
    const wd = (d.getDay() + 6) % 7;
    if (!settings?.working_days?.includes(wd)) return false;
    if (settings?.blocked_dates?.includes(toISODate(d))) return false;
    return true;
  };

  const monthLabel = `${MONTHS[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`;
  const canPrev = viewMonth.getFullYear() > today.getFullYear() || viewMonth.getMonth() > today.getMonth();

  return (
    <div className="c-card max-w-md mx-auto">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => canPrev && setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} className={`p-3 ${canPrev ? "text-white hover:c-red" : "text-zinc-700 cursor-not-allowed"}`} aria-label="Previous month" data-testid="cal-prev">
          <ChevronLeft size={20} />
        </button>
        <div className="text-lg" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{monthLabel}</div>
        <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} className="p-3 text-white hover:c-red" aria-label="Next month" data-testid="cal-next">
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-[10px] tracking-[0.16em] uppercase c-muted mb-2">
        {WEEKDAYS.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = toISODate(d);
          const pickable = isPickable(d);
          const selected = selectedDate === iso;
          return (
            <button
              key={i}
              disabled={!pickable}
              onClick={() => pickable && onPick(iso)}
              className={`h-11 text-sm transition-colors ${
                selected ? "bg-red-700 text-white" : pickable ? "text-white hover:bg-white/5" : "text-zinc-700 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0 }}
              data-testid={`cal-day-${iso}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SlotPicker({ date, slots, loading, onPick, onBack }) {
  return (
    <div className="max-w-md mx-auto mt-6">
      <button onClick={onBack} className="text-xs c-muted hover:text-white mb-4 flex items-center gap-1 py-2" data-testid="slots-back">
        <ChevronLeft size={14} /> Change date
      </button>
      <div className="text-base text-white mb-1" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
        {fmtDateLong(date)}
      </div>
      <div className="text-xs c-muted mb-5 flex items-center gap-1.5"><Clock size={12} className="c-red" /> All times in IST</div>
      {loading ? (
        <div className="c-card text-center c-muted py-8 text-sm">Loading slots…</div>
      ) : slots.length === 0 ? (
        <div className="c-card text-center c-muted py-8 text-sm">No slots available. Try another date.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
          {slots.map((t) => (
            <button
              key={t}
              onClick={() => onPick(t)}
              className="py-3.5 text-sm border border-white/10 hover:border-red-600 hover:bg-red-700/10 text-white"
              style={{ borderRadius: 0 }}
              data-testid={`slot-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
